import React, { useEffect, useState, useRef } from "react";
import "./VipCastle.styles.css";
import useGlobalStore from "../../../../store/global.store";
import { useTV } from "../../../../hooks/useTV";
import { useRemote } from "../../../../hooks/useRemote";
import { useStreamTime } from "../../../../hooks/useStreamTime";
import VipOffStreamView from "../VipOffStreamView";
import VipSubCheck from "../VipSubCheck";
import { useConfig } from "../../../../hooks/useConfig";
import { useBeaconShow } from "../../../../hooks/useBeaconShow";
import BandWakeUpOverlay from "../BandWakeUpOverlay";
import VipTakeControlBadge from "../VipTakeControlBadge";

function VipCastle() {
  const { isTv } = useTV();
  const { updateView } = useRemote();
  const {
    loading,
    showVipStream,
    showVipStreamLoader,
    getMagicKingdomShowTimeText,
  } = useStreamTime();
  const { config } = useConfig();

  const vipStreamSrc =
    config?.VIPstreamUrl === 1
      ? isTv
        ? "https://encoder3.mywaitserver.com/live/player/720p/"
        : "https://encoder3.mywaitserver.com/live/player/480p/"
      : config?.VIPstreamUrl === 2
      ? "https://encoder2.mywaitserver.com/live/player/"
      : config?.VIPstreamUrl === 3
      ? isTv
        ? "https://encoder.mywaitserver.com/live/player/720p/"
        : "https://encoder.mywaitserver.com/live/player/480p/"
      : config?.VIPstreamUrl === 4
      ? "https://encoder4.mywaitserver.com/live/player/"
      : isTv
      ? "https://encoder3.mywaitserver.com/live/player/720p/"
      : "https://encoder3.mywaitserver.com/live/player/480p/";

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  // const setShowVipSelector = useGlobalStore(
  //   (state) => state.setShowVipSelector
  // );
  // const setSelectedVipDoor = useGlobalStore(
  //   (state) => state.setSelectedVipDoor
  // );
  const showVipSelector = useGlobalStore((state) => state.showVipSelector);

  const { wakeUpOverlay, dismissWakeUp, exitShow } = useBeaconShow();

  const [lightOn, setLightOn] = useState(true);
  const [infoOn, setInfoOn] = useState(false);
  const [focusedButton, setFocusedButton] = useState("info");

  // Iframe positioning values - using constants instead of state
  const iframeStyle = config?.iframeStyles?.castle || {
    display: {
      top: "0px",
      left: "-50px",
      width: "1280px",
      height: "720px",
      zoom: 1,
      origin: "top left",
    },
    tv: {
      top: "75px",
      left: "125px",
      width: "1600px",
      height: "900px",
      zoom: 1,
      origin: "top left",
    },
  };

  const infoButtonRef = useRef(null);
  const lightButtonRef = useRef(null);
  const exitButtonRef = useRef(null);

  const toggleLight = () => {
    if (showVipSelector) return;
    const clickSound = new Audio("/static/vip/click.mp3");
    clickSound.play();
    setLightOn((prev) => !prev);
  };

  const toggleInfo = () => {
    if (showVipSelector) return;
    setInfoOn((prev) => !prev);
  };

  const handleExit = () => {
    exitShow();
    setShowBottomBar(false);
    updateView("VIP_door_2_selector", () => {});
  };

  // Set bottom bar to false on mount
  useEffect(() => {
    setShowBottomBar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus info button when component mounts (for TV mode)
  useEffect(() => {
    if (isTv && infoButtonRef.current && !showVipSelector) {
      infoButtonRef.current.focus();
      setFocusedButton("info");
    }
  }, [isTv, showVipSelector]);

  // Keyboard navigation for TV mode
  useEffect(() => {
    if (isTv && !showVipSelector) {
      const handleKeyDown = (e) => {
        const buttons = ["info", "light", "exit"];
        const currentIndex = buttons.indexOf(focusedButton);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          // Navigate to next button or wrap to first
          const nextIndex = (currentIndex + 1) % buttons.length;
          const nextButton = buttons[nextIndex];
          setFocusedButton(nextButton);

          // Focus the appropriate button
          if (nextButton === "info" && infoButtonRef.current) {
            infoButtonRef.current.focus();
          } else if (nextButton === "light" && lightButtonRef.current) {
            lightButtonRef.current.focus();
          } else if (nextButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          // Navigate to previous button or wrap to last
          const prevIndex =
            (currentIndex - 1 + buttons.length) % buttons.length;
          const prevButton = buttons[prevIndex];
          setFocusedButton(prevButton);

          // Focus the appropriate button
          if (prevButton === "info" && infoButtonRef.current) {
            infoButtonRef.current.focus();
          } else if (prevButton === "light" && lightButtonRef.current) {
            lightButtonRef.current.focus();
          } else if (prevButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "Enter") {
          e.preventDefault();

          // Handle Enter key for the focused button
          if (focusedButton === "info") {
            toggleInfo();
          } else if (focusedButton === "light") {
            toggleLight();
          } else if (focusedButton === "exit") {
            handleExit();
          }
        }
      };

      // Add event listener
      window.addEventListener("keydown", handleKeyDown);

      // Clean up
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, focusedButton, showVipSelector]);

  // Helper function to apply focus styling
  const getFocusStyle = (buttonId) => {
    if (isTv && focusedButton === buttonId && !showVipSelector) {
      return {
        outline: "none",
        boxShadow: "0 0 0 4px #3ADF3C",
      };
    }
    return {};
  };

  // Get current iframe style based on device type
  const getCurrentIframeStyle = () => {
    const currentSettings = isTv ? iframeStyle.tv : iframeStyle.display;
    return {
      position: "absolute",
      top: currentSettings.top,
      left: currentSettings.left,
      width: currentSettings.width,
      height: currentSettings.height,
      transform: `scale(${currentSettings.zoom})`,
      transformOrigin: currentSettings.origin,
      border: "none",
      zIndex: 0,
      imageRendering: "auto",
    };
  };

  return (
    <div className="vip_roof-app-container">
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontSize: "30px",
            color: "blue",
            fontFamily: "Lato",
            zIndex: 50,
          }}
        >
          <span>Loading...</span>
        </div>
      ) : (
        <>
          {/* Blue info bar - show only if infoOn and we have show time text */}
          {infoOn && getMagicKingdomShowTimeText() && (
            <div
              className="vip_roof-info-bar"
              style={{
                fontSize: isTv ? "40px" : "30px",
                height: isTv ? "120px" : undefined,
                lineHeight: isTv ? "110px" : undefined,
                fontFamily: "lato",
              }}
            >
              Magic Kingdom Fireworks: {getMagicKingdomShowTimeText()}
            </div>
          )}

          {showVipStream === true ? (
            <>
              {/* Stream loader animation */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  opacity: showVipStreamLoader ? 1 : 0,
                  transition: showVipStreamLoader ? "none" : "opacity 3s",
                  pointerEvents: showVipStreamLoader ? "auto" : "none",
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 60,
                  backgroundColor: "black",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                  }}
                  src={
                    isTv
                      ? "/static/vip/transition/tv/castle/on.jpg"
                      : "/static/vip/transition/display/castle/on.jpg"
                  }
                  alt=""
                />
              </div>

              {/* Live stream iframe with inline styles */}
              <iframe
                src={vipStreamSrc}
                title="Live Stream Background"
                style={getCurrentIframeStyle()}
                allow="autoplay"
                allowFullScreen
              />
              <img
                src="/static/stream/livelogo.png"
                alt="Live"
                style={{
                  position: "absolute",
                  top: isTv ? "-10px" : "0px",
                  right: isTv ? "20px" : "10px",
                  width: isTv ? "580px" : "340px",
                  zIndex: 20,
                }}
              />

              <img
                src={
                  isTv
                    ? "/static/vip/castletvoff.png"
                    : "/static/vip/castledisplayoff.png"
                }
                alt="TV Roof Off"
                className="vip_roof-fw vip_roof-fw4"
              />
              {lightOn && (
                <img
                  src={
                    isTv
                      ? "/static/vip/castletvon.png"
                      : "/static/vip/castledisplayon.png"
                  }
                  alt="TV Roof On"
                  className="vip_roof-fw vip_roof-fw3"
                />
              )}
            </>
          ) : (
            showVipStream === false && (
              <VipOffStreamView
                bgImage={
                  isTv
                    ? "/static/vip/transition/tv/castle/off.jpg"
                    : "/static/vip/transition/display/castle/off.jpg"
                }
              />
            )
          )}

          {/* Control buttons - always shown */}
          {/* Info button */}
          {/* INFO */}
{/* CONTROLS */}
{isTv ? (
  <>
    {/* TV — use original CSS classes & sizes/positions */}
    <button
      ref={infoButtonRef}
      className={`vip_roof-info-button ${infoOn ? "on" : "off"}`}
      onClick={toggleInfo}
      onFocus={() => !showVipSelector && setFocusedButton("info")}
      onBlur={() => (isTv ? null : setFocusedButton(null))}
      style={getFocusStyle("info")}           // green ring when focused
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Info"
    >
      <span>i</span>
    </button>

    <button
      ref={lightButtonRef}
      className={`vip_roof-lightbulb-button ${lightOn ? "on" : "off"}`}
      onClick={toggleLight}
      onFocus={() => !showVipSelector && setFocusedButton("light")}
      onBlur={() => (isTv ? null : setFocusedButton(null))}
      style={getFocusStyle("light")}          // green ring when focused
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Light"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="#022758" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M9 18h6" /><path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 1 7 7c0 2.8-1.5 4.2-2.4 5-1 .9-1.6 1.5-1.6 3H9c0-1.5-.6-2.1-1.6-3C6.5 13.2 5 11.8 5 9a7 7 0 0 1 7-7z" />
      </svg>
    </button>

    <button
      ref={exitButtonRef}
      className="vip_roof-exit-button"
      onClick={handleExit}
      onFocus={() => !showVipSelector && setFocusedButton("exit")}
      onBlur={() => (isTv ? null : setFocusedButton(null))}
      style={getFocusStyle("exit")}           // green ring when focused
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Exit"
    >
      EXIT
    </button>
  </>
) : (
  <>
    {/* DISPLAY — simple, bounce-free inline divs */}
    <div
      ref={infoButtonRef}
      role="button"
      onClick={toggleInfo}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleInfo(); } }}
      onFocus={() => !showVipSelector && setFocusedButton("info")}
      onBlur={() => setFocusedButton(null)}
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Info"
      style={{
        zIndex: 4,
        position: "absolute", left: "30px", bottom: "255px",
        width: 75, height: 75, borderRadius: "50%", backgroundColor: "white",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
        outline: "none", transition: "none", fontFamily: "Lato",
      }}
    >
      <span style={{ fontSize: 32, fontWeight: "bold", fontStyle: "italic", color: "#022758", lineHeight: 1 }}>i</span>
    </div>

    <div
      ref={lightButtonRef}
      role="button"
      onClick={toggleLight}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleLight(); } }}
      onFocus={() => !showVipSelector && setFocusedButton("light")}
      onBlur={() => setFocusedButton(null)}
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Light"
      style={{
        zIndex: 4,
        position: "absolute", left: "30px", bottom: "155px",
        width: 75, height: 75, borderRadius: "50%", backgroundColor: "white",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
        outline: "none", transition: "none", fontFamily: "Lato",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="#022758" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M9 18h6" /><path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 1 7 7c0 2.8-1.5 4.2-2.4 5-1 .9-1.6 1.5-1.6 3H9c0-1.5-.6-2.1-1.6-3C6.5 13.2 5 11.8 5 9a7 7 0 0 1 7-7z" />
      </svg>
    </div>

    <div
      ref={exitButtonRef}
      role="button"
      onClick={handleExit}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleExit(); } }}
      onFocus={() => !showVipSelector && setFocusedButton("exit")}
      onBlur={() => setFocusedButton(null)}
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Exit"
      style={{
        zIndex: 4,
        position: "absolute", left: "30px", bottom: "55px",
        width: 80, height: 80, borderRadius: "50%", backgroundColor: "red",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
        outline: "none", transition: "none", fontFamily: "Lato",
      }}
    >
      <span style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>EXIT</span>
    </div>
 
</>
)}


</>
)}
      <BandWakeUpOverlay
        show={wakeUpOverlay.show}
        secondsLeft={wakeUpOverlay.secondsLeft}
        onDismiss={dismissWakeUp}
      />
      <VipTakeControlBadge holdWhile={showVipStreamLoader || showVipStream !== true} />
      <VipSubCheck />
    </div>
  );
}

export default VipCastle;
